import React from 'react';
import { Card, Form, Input, DatePicker, Select, Button, Space, Row, Col, InputNumber } from 'antd';
import { Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TaskTimelog } from '../../services/tasks';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface User {
  id: number;
  name: string;
  email: string;
}

interface OverviewTabProps {
  form: any;
  isEditing: boolean;
  currentStatus: 'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed';
  canViewDetails: boolean;
  canEditDetails: boolean;
  canViewDetailsPlan: boolean;
  canEditDetailsPlan: boolean;
  canViewActual: boolean;
  canEditActual: boolean;
  canDelete: boolean;
  customerType: 'personal' | 'business';
  customers: any[];
  businesses: any[];
  taskTypes: any[];
  taskTemplates: any[];
  quotations: any[];
  invoices: any[];
  letters: any[];
  dispatches: any[];
  timelogs: TaskTimelog[];
  id?: string;
  handleCustomerTypeChange: (type: 'personal' | 'business') => void;
  handleCustomerChange: (customerId: number) => void;
  handleBusinessChange: (businessId: number) => void;
  handleTaskTemplateChange: (templateId: number | null) => void;
  handleQuotationChange: (quotationId: number) => void;
  handleCreatedDateChange: (date: dayjs.Dayjs | null) => void;
  getDisplayValue: (fieldName: string, type: 'select' | 'date' | 'number' | 'text', options?: any[]) => string;
  calculateTotalHours: () => number;
  timelogForm: { date: dayjs.Dayjs; hour: number; description: string };
  setTimelogForm: React.Dispatch<React.SetStateAction<{ date: dayjs.Dayjs; hour: number; description: string }>>;
  handleAddTimelog: () => void;
  handleDeleteTimelog: (timelogId: number | string) => void;
  user?: User;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  form,
  isEditing,
  currentStatus,
  canViewDetails,
  canEditDetails,
  canViewDetailsPlan,
  canEditDetailsPlan,
  canViewActual,
  canEditActual,
  canDelete,
  customerType,
  customers,
  businesses,
  taskTypes,
  taskTemplates,
  quotations,
  invoices,
  letters,
  dispatches,
  timelogs,
  handleCustomerTypeChange,
  handleCustomerChange,
  handleBusinessChange,
  handleTaskTemplateChange,
  handleQuotationChange,
  handleCreatedDateChange,
  getDisplayValue,
  calculateTotalHours,
  timelogForm,
  setTimelogForm,
  handleAddTimelog,
  handleDeleteTimelog,
  user,
}) => {
  const navigate = useNavigate();
  const allowEditDetails = isEditing && canEditDetails;
  const allowEditDetailsPlan = isEditing && canEditDetailsPlan;
  const allowEditActual = isEditing && canEditActual;
  const showLeftColumn = canViewDetails || canViewDetailsPlan;
  const showRightColumn = canViewActual;
  const leftColSpan = showLeftColumn && showRightColumn ? 12 : 24;
  const rightColSpan = showLeftColumn && showRightColumn ? 12 : 24;
  const renderReferenceLinks = (items: any[], basePath: string) => {
    if (!items || (Array.isArray(items) && items.length === 0)) {
      return '-';
    }
    const list = Array.isArray(items) ? items : [items];
    return list.map((item, index) => {
      const fallbackId = item?.id ?? null;
      const label =
        item?.reference_number
        || item?.referenceNumber
        || (fallbackId ? `#${fallbackId.toString().padStart(7, '0')}` : '-');
      if (!fallbackId) {
        return <span key={`ref-${index}`}>{label}</span>;
      }
      return (
        <React.Fragment key={fallbackId}>
          <Button
            type="link"
            disabled={false}
            onClick={() => navigate(`/${basePath}/${fallbackId}/edit`)}
            style={{ padding: 0, height: 'auto' }}
          >
            {label}
          </Button>
          {index < list.length - 1 ? <span>, </span> : null}
        </React.Fragment>
      );
    });
  };
  return (
    <Row gutter={24}>
      {/* Left Column */}
      {showLeftColumn && (
        <Col span={leftColSpan}>
          <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {canViewDetails && (
                  <>
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>ID</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Form.Item name="reference_number" style={{ margin: 0 }}>
                            <Input placeholder="Enter Reference No." />
                          </Form.Item>
                        ) : (
                          <div>{form.getFieldValue('reference_number') || '-'}</div>
                        )}
                      </td>
                    </tr>
                    {/* Customer Type - 单独一行，合并后三列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Customer</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Space>
                            <Button
                              type={customerType === 'personal' ? 'primary' : 'default'}
                              onClick={() => handleCustomerTypeChange('personal')}
                              size="small"
                            >
                              Personal
                            </Button>
                            <Button
                              type={customerType === 'business' ? 'primary' : 'default'}
                              onClick={() => handleCustomerTypeChange('business')}
                              size="small"
                            >
                              Business
                            </Button>
                          </Space>
                        ) : (
                          <div>
                            {customerType === 'personal' ? 'Personal' : 'Business'}
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Customer/Business - 单独一行，合并后三列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          customerType === 'personal' ? (
                            <Form.Item
                              name="customer_id"
                              rules={[{ required: true, message: 'Please select customer' }]}
                              style={{ margin: 0 }}
                            >
                              <Select
                                placeholder="Select Customer"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                onChange={handleCustomerChange}
                              >
                                {customers.map((customer) => (
                                  <Select.Option key={customer.id} value={customer.id}>
                                    {customer.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          ) : (
                            <Form.Item
                              name="business_id"
                              rules={[{ required: true, message: 'Please select business' }]}
                              style={{ margin: 0 }}
                            >
                              <Select
                                placeholder="Select Business"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                onChange={handleBusinessChange}
                              >
                                {businesses.map((business) => (
                                  <Select.Option key={business.id} value={business.id}>
                                    {business.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          )
                        ) : (
                          <div>
                            {customerType === 'personal' 
                              ? getDisplayValue('customer_id', 'select', customers)
                              : getDisplayValue('business_id', 'select', businesses)
                            }
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Task - 单独一行，合并后三列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Task</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Form.Item name="task_template_id" style={{ margin: 0 }}>
                            <Select
                              placeholder="Select Task"
                              allowClear
                              onChange={handleTaskTemplateChange}
                            >
                              {taskTemplates.map((template) => (
                                <Select.Option key={template.id} value={template.id}>
                                  {template.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('task_template_id', 'select', taskTemplates)}
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Year - 单独一行，合并后三列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Period</strong>
                      </td>
                      <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Form.Item
                            name="year"
                            style={{ margin: 0 }}
                            rules={[
                              {
                                pattern: /^(0[1-9]|1[0-2])\/\d{4}$/,
                                message: 'Use MM/YYYY',
                              },
                            ]}
                            getValueFromEvent={(_date, dateString) => dateString || undefined}
                            getValueProps={(value) => ({
                              value: value ? dayjs(value, 'MM/YYYY') : null,
                            })}
                          >
                            <DatePicker picker="month" format="MM/YYYY" style={{ width: '100%' }} placeholder="MM/YYYY" />
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('year', 'text')}
                          </div>
                        )}
                      </td>
                    </tr>
                  </>
                )}
                {/* Create 和 Due - 同一行，各占两列 */}
                {canViewDetailsPlan && (isEditing || currentStatus !== 'Plan') && (
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Create</strong>
                    </td>
                    <td
                      style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}
                      colSpan={canViewDetails ? 1 : 3}
                    >
                      {allowEditDetailsPlan ? (
                        <Form.Item name="created_date" style={{ margin: 0 }}>
                          <DatePicker 
                            style={{ width: '100%' }} 
                            format="DD/MM/YYYY" 
                            placeholder="Enter Create Date"
                            onChange={handleCreatedDateChange}
                          />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('created_date', 'date')}
                        </div>
                      )}
                    </td>
                    {canViewDetails && (
                      <>
                        <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Due</strong>
                        </td>
                        <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                          {allowEditDetails ? (
                            <Form.Item name="due_date" style={{ margin: 0 }}>
                              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Due Date" />
                            </Form.Item>
                          ) : (
                            <div>
                              {getDisplayValue('due_date', 'date')}
                            </div>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                )}
                {!canViewDetailsPlan && canViewDetails && (
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Due</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {allowEditDetails ? (
                        <Form.Item name="due_date" style={{ margin: 0 }}>
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Due Date" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('due_date', 'date')}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {canViewDetails && (
                  <>
                    {/* Plan 和 Deadline - 同一行，各占两列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Plan</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Form.Item name="plan_date" style={{ margin: 0 }}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Plan Date" />
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('plan_date', 'date')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Deadline</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        {allowEditDetails ? (
                          <Form.Item name="deadline" style={{ margin: 0 }}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Deadline" />
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('deadline', 'date')}
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* Estimate 和 Actual - 同一行，各占两列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Estimate</strong>
                      </td>
                      <td
                        style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}
                        colSpan={canViewActual ? 1 : 3}
                      >
                        {allowEditDetails ? (
                          <Form.Item name="plan_hour" style={{ margin: 0 }}>
                            <InputNumber style={{ width: '100%' }} placeholder="Enter Estimate Hour" min={0} />
                          </Form.Item>
                        ) : (
                          <div>
                            {getDisplayValue('plan_hour', 'number')}
                          </div>
                        )}
                      </td>
                      {canViewActual && (
                        <>
                          <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                            <strong>Actual</strong>
                          </td>
                          <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                            <div>
                              {calculateTotalHours() || '-'}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                    {/* Letter ID 和 Dispatch ID - 同一行，各占两列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Letter ID</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        <div>{renderReferenceLinks(letters, 'letters')}</div>
                      </td>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Dispatch ID</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        <div>{renderReferenceLinks(dispatches, 'dispatches')}</div>
                      </td>
                    </tr>
                    {/* Quotation ID 和 Invoice ID - 同一行，各占两列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Quotation ID</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        <div>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => {
                              const quotationId = getFieldValue('quotation_id');
                              const quotation = quotations.find((q: any) => q.id === quotationId);
                              if (!quotationId) {
                                return '-';
                              }

                              const label = quotation?.quotation_number || `#${quotationId}`;

                              return (
                                <Button
                                  type="link"
                                  disabled={false}
                                  onClick={() => navigate(`/quotations/${quotationId}/edit`)}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {label}
                                </Button>
                              );
                            }}
                          </Form.Item>
                        </div>
                      </td>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Invoice ID</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        <div>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.quotation_id !== currentValues.quotation_id} noStyle>
                            {({ getFieldValue }) => {
                              const quotationId = getFieldValue('quotation_id');
                              const quotation = quotations.find((q: any) => q.id === quotationId);
                              const invoice =
                                quotation?.invoice || invoices.find((inv: any) => inv.quotation_id === quotationId);
                              const invoiceId = invoice?.id ?? quotation?.invoice_id;
                              if (!invoiceId) {
                                return '-';
                              }

                              const label = invoice?.invoice_number || `#${invoiceId}`;

                              return (
                                <Button
                                  type="link"
                                  disabled={false}
                                  onClick={() => navigate(`/invoices/${invoiceId}/edit`)}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {label}
                                </Button>
                              );
                            }}
                          </Form.Item>
                        </div>
                      </td>
                    </tr>
                    {/* Team 和 Staff - 同一行，各占两列 */}
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Team</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        <div>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.quotation_id !== currentValues.quotation_id} noStyle>
                            {({ getFieldValue }) => {
                              const quotationId = getFieldValue('quotation_id');
                              const quotation = quotations.find((q: any) => q.id === quotationId);
                              return quotation?.team?.name || '-';
                            }}
                          </Form.Item>
                        </div>
                      </td>
                      <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Staff</strong>
                      </td>
                      <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                        <div>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.quotation_id !== currentValues.quotation_id} noStyle>
                            {({ getFieldValue }) => {
                              const quotationId = getFieldValue('quotation_id');
                              const quotation = quotations.find((q: any) => q.id === quotationId);
                              return quotation?.staff?.name || '-';
                            }}
                          </Form.Item>
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </Card>
        </Col>
      )}

      {/* Right Column */}
      {showRightColumn && (
        <Col span={rightColSpan}>
          {/* Actual Timelog */}
          <Card 
            title="Actual" 
            bodyStyle={{ padding: '16px' }}
          >
            {/* Actual Timelogs List */}
            <div style={{ marginBottom: 24 }}>
              {timelogs.length === 0 ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No timelogs yet
                </div>
              ) : (
                timelogs.map((timelog) => (
                  <div 
                    key={timelog.id} 
                    style={{ 
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                    }}
                  >
                    <Space>
                      <span>{timelog.hour}h</span>
                      <span>{dayjs(timelog.date).format('DD/MM/YYYY')}</span>
                      <span>{timelog.description || 'Description'}</span>
                    </Space>
                    {allowEditActual && canDelete && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteTimelog(timelog.id)}
                        style={{ padding: 0 }}
                      />
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add Timelog Section */}
            {allowEditActual && (
              <div style={{ paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <div style={{ marginBottom: 12, fontWeight: 500, color: '#595959' }}>Add Timelog</div>
              <Row gutter={8} style={{ marginBottom: 12 }}>
                <Col span={12}>
                  <div style={{ marginBottom: 4, color: '#595959', fontSize: '12px' }}>Date</div>
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    placeholder="Enter Date"
                    value={timelogForm.date}
                    onChange={(date) => setTimelogForm(prev => ({ ...prev, date: date || dayjs() }))}
                  />
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4, color: '#595959', fontSize: '12px' }}>Hour</div>
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Enter Hour"
                    min={0}
                    value={timelogForm.hour}
                    onChange={(hour) => setTimelogForm(prev => ({ ...prev, hour: hour || 0 }))}
                  />
                </Col>
              </Row>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4, color: '#595959', fontSize: '12px' }}>Description</div>
                <Input
                  placeholder="Enter Description"
                  value={timelogForm.description}
                  onChange={(e) => setTimelogForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddTimelog}
                style={{ width: '100%' }}
              >
                Add Timelog
              </Button>
              </div>
            )}
          </Card>
        </Col>
      )}
    </Row>
  );
};
